import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnInit, signal } from '@angular/core';
import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { PermissionRole } from 'app/core/enums/generated/PermissionRole';
import { SecurityService } from 'app/core/security/security.service';
import { NotificationService } from 'app/notification.service';
import { FdaRecallVm } from 'app/shared/generated/NdcDirectory/Models/FdaRecallVm';
import { NavigationService } from 'app/shared/navigation/navigation.service';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'pcg-recalls-widget',
    templateUrl: './recalls-widget.component.html',
    styleUrl: './recalls-widget.component.scss',
    imports: [TableModule, TooltipModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecallsWidgetComponent implements OnInit {

    readonly navService = inject(NavigationService);
    private readonly http = inject(HttpClient);
    private readonly toastr = inject(NotificationService);
    private readonly sec = inject(SecurityService);
    private readonly cdr = inject(ChangeDetectorRef);

    readonly recalls = signal<FdaRecallVm[]>([]);
    readonly displayLimit = signal(5);
    readonly isScrolledToBottom = signal(false);
    readonly loading = signal(false);
    readonly hasRecalls = signal(false);

    readonly filteredRecalls = computed(() => this.recalls().slice(0, this.displayLimit()));
    readonly hasMoreRecalls = computed(() => this.recalls().length > this.displayLimit());

    ngOnInit(): void {
        const hasNdcModule = this.sec.hasModuleAccess([BusinessAreaEnum.NDC], SecurityService.setMinRole(PermissionRole.User));
        if (hasNdcModule) { this.loadRecalls(); }
    }

    showMore(): void {
        this.displayLimit.update(current => current + 5);
    }

    onScroll(event: Event): void {
        const element = event.target as HTMLElement;
        const atBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 1;
        this.isScrolledToBottom.set(atBottom);

        if (
            this.navService.isMobile()
            && element.scrollTop + element.clientHeight >= element.scrollHeight - 100
        ) {
            if (this.hasMoreRecalls()) { this.showMore(); }
        }
    }

    private loadRecalls(): void {
        this.loading.set(true);
        const url = 'api/NdcDirectory/NdcDirectory/GetFdaRecallListWidget/?state=FL&status=Ongoing&isInFormulary=true';

        this.http.get<FdaRecallVm[]>(url).subscribe({
            next: (lst) => {
                this.recalls.set(lst);
                this.hasRecalls.set(lst.length > 0);
                this.loading.set(false);
                this.cdr.markForCheck();
            },
            error: (error) => {
                this.toastr.showError('Error fetching recalls');
                console.error('Error fetching recalls:', error);
                this.loading.set(false);
                this.cdr.markForCheck();
            },
        });
    }
}
